import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { CookieService } from './cookie.service';
import { Thumbmark } from '@thumbmarkjs/thumbmarkjs';
import {
	DeviceIdResolutionSource,
	LocalStorageKeys,
	ITfaCompletedResponse,
	ITfaSendResponse,
	ITfaSmsCodeBody,
	ITfaSmsCodeResponse,
	TwoFactorChannel,
} from '../models/auth.models';
import { coordinateDeviceIdResolution } from '../utils/device-id-coordination.util';
import { environment } from 'src/environments/environment';

const THUMBMARK_OPTIONS = {
	stabilize: ['private', 'iframe', 'always'],
	exclude: ['permissions'],
	timeout: 5000,
};

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private router = inject(Router);
	private cookieService = inject(CookieService);
	private http = inject(HttpClient);

	private dialogTokenHasExpired: any = null;
	private deviceId: string | null = null;
	private deviceIdResolvePromise: Promise<string> | null = null;
	private lastDeviceIdResolutionSource: DeviceIdResolutionSource | null = null;
	private readonly thumbmarkClient = new Thumbmark(THUMBMARK_OPTIONS);
	private urlQr: string = '';

	private parseJwt(token: any): any {
		try {
			const base64Url = token.split('.')[1];
			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split('')
					.map((c) => {
						return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
					})
					.join(''),
			);

			return JSON.parse(jsonPayload);
		} catch (error) {
			return false;
		}
	}

	private checkIfTokenHasExpired(): boolean {
		const access_token: string | null =
			localStorage.getItem(LocalStorageKeys.access_token) ||
			this.cookieService.get(LocalStorageKeys.access_token);
		if (access_token && access_token !== '') {
			// TODO: tengo que revisar si el token ha expirado en el iframe?
			return false;
			const jwt = this.parseJwt(access_token);
			if (!jwt) {
				return true;
			}
			const exp = jwt.exp * 1000;

			return new Date().getTime() >= exp;
		}

		return true;
	}

	public getToken(): any {
		if (this.checkIfTokenHasExpired()) {
			console.warn('JWT expired');

			localStorage.removeItem(LocalStorageKeys.access_token);
			this.cookieService.delete(LocalStorageKeys.access_token);
			if (this.dialogTokenHasExpired == null) {
				this.dialogTokenHasExpired = true;
			}
			return;
		}
		return (
			localStorage.getItem(LocalStorageKeys.access_token) || this.cookieService.get(LocalStorageKeys.access_token)
		);
	}

	public setAuthToken(token: string): void {
		localStorage.setItem(LocalStorageKeys.auth_token, token);
	}

	public getAuthToken(): string | null {
		return localStorage.getItem(LocalStorageKeys.auth_token);
	}

	public setAccessToken(token: string): void {
		localStorage.setItem(LocalStorageKeys.access_token, token);
	}

	public clearAccessToken(): void {
		// localStorage.removeItem(LocalStorageKeys.access_token);
		// localStorage.removeItem(LocalStorageKeys.auth_token);
		localStorage.clear();
	}

	public getQRUrl(): string {
		if ((environment as any).isLocal && this.urlQr) {
			try {
				const parsed = new URL(this.urlQr);
				return window.location.origin + parsed.pathname + parsed.search;
			} catch {
				return this.urlQr;
			}
		}
		return this.urlQr;
	}

	public setQRUrl(url: string): void {
		this.urlQr = url;
	}

	public logout(): void {
		this.cookieService.delete(LocalStorageKeys.access_token, '/');
		this.router.navigate(['']);
	}

	/** Identificador de dispositivo lógico ya resuelto en esta sesión de pestaña. */
	public getDeviceId(): string {
		return this.deviceId ?? '';
	}

	public getLastDeviceIdResolutionSource(): DeviceIdResolutionSource | null {
		return this.lastDeviceIdResolutionSource;
	}

	/**
	 * Resuelve y persiste `device_id`: memoria → localStorage (por token del enlace) → Thumbmark → UUID fallback.
	 */
	public async resolveDeviceId(): Promise<string> {
		if (this.deviceId) {
			this.lastDeviceIdResolutionSource = DeviceIdResolutionSource.Memory;
			return this.deviceId;
		}
		if (this.deviceIdResolvePromise) {
			return this.deviceIdResolvePromise;
		}

		this.deviceIdResolvePromise = this.resolveDeviceIdInternal();
		try {
			return await this.deviceIdResolvePromise;
		} finally {
			this.deviceIdResolvePromise = null;
		}
	}

	private async resolveDeviceIdInternal(): Promise<string> {
		const storageKey = this.buildTokenStorageKey();

		const outcome = await coordinateDeviceIdResolution(storageKey, async () => {
			const fromThumbmark = await this.resolveThumbmarkId();
			if (fromThumbmark) {
				return { id: fromThumbmark, meta: DeviceIdResolutionSource.Thumbmark };
			}
			return { id: this.createFallbackDeviceId(), meta: DeviceIdResolutionSource.Fallback };
		});

		if (outcome.origin === 'shared') {
			this.assignDeviceId(outcome.id, DeviceIdResolutionSource.LocalStorage);
			return outcome.id;
		}

		this.assignDeviceId(outcome.id, outcome.meta);
		return outcome.id;
	}

	private async resolveThumbmarkId(): Promise<string | null> {
		try {
			const result = await this.thumbmarkClient.get();
			const thumbmark = result?.thumbmark?.trim();
			return thumbmark || null;
		} catch (error) {
			console.error('Error generating fingerprint:', error);
			return null;
		}
	}

	private assignDeviceId(id: string, source: DeviceIdResolutionSource): void {
		this.deviceId = id;
		this.lastDeviceIdResolutionSource = source;
	}

	private buildTokenStorageKey(): string {
		return `${LocalStorageKeys.device_id_storage_prefix}:${this.getDeviceIdStorageScope()}`;
	}

	private getDeviceIdStorageScope(): string {
		const token =
			this.getAuthToken()?.trim() ||
			localStorage.getItem(LocalStorageKeys.access_token)?.trim() ||
			this.cookieService.get(LocalStorageKeys.access_token)?.trim() ||
			'';
		if (!token) {
			return 'notoken';
		}
		return this.hashScope(token);
	}

	private hashScope(value: string): string {
		let hash = 0;
		for (let i = 0; i < value.length; i++) {
			hash = (hash << 5) - hash + value.charCodeAt(i);
			hash |= 0;
		}
		return Math.abs(hash).toString(36);
	}

	private createFallbackDeviceId(): string {
		if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
			return crypto.randomUUID();
		}
		return `pwcs-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
	}

	private getAuthHeaders(): HttpHeaders {
		const token = this.getToken();
		return new HttpHeaders({
			Authorization: 'Bearer ' + (token ?? ''),
		});
	}

	/**
	 * Envía SMS o mensaje WhatsApp de verificación (máx. 3 envíos por operación en backend).
	 */
	public sendTfa(channel: TwoFactorChannel): Observable<ITfaSendResponse> {
		const body = { type: channel };
		return this.http.post<ITfaSendResponse>(`${environment.identity2}/tfa/send`, body, {
			headers: this.getAuthHeaders(),
		});
	}

	/**
	 * Valida el código OTP recibido por SMS.
	 */
	public submitSmsTfaCode(body: ITfaSmsCodeBody): Observable<ITfaSmsCodeResponse> {
		return this.http.post<ITfaSmsCodeResponse>(`${environment.identity2}/tfa/sms-code`, body, {
			headers: this.getAuthHeaders(),
		});
	}

	/**
	 * Consulta si el usuario completó la confirmación en WhatsApp (polling).
	 */
	public getTfaCompleted(tfaId: string): Observable<ITfaCompletedResponse> {
		const id = encodeURIComponent(tfaId);
		return this.http.get<ITfaCompletedResponse>(`${environment.identity2}/tfa/${id}/completed`, {
			headers: this.getAuthHeaders(),
		});
	}
}
